import { Button, Divider, Stack, Typography } from '@mui/material';
import { AdultAssociation, SyncUser, WithAvatar, WithName } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { UserContexts } from '../../../constants/userContexts';
import { getRouteModalPathname } from '../../../helpers/misc';
import { getAdultUserTypes, getUserFullName } from '../../../helpers/users';
import useAppLocation from '../../../hooks/useAppLocation';

interface UserNavProps {
  user: SyncUser;
  onClick: VoidFunction;
  onProfileOpen: VoidFunction;
  logOut: VoidFunction;
}

const UserNav: React.FC<UserNavProps> = ({ user, onClick, onProfileOpen, logOut }) => {
  const navigate = useNavigate();
  const location = useAppLocation();

  const userTypes = getAdultUserTypes(user);

  const isParent = userTypes?.includes('parent');

  const handleMyProfileClick = useCallback(() => {
    onProfileOpen();
    onClick();
  }, [onClick, onProfileOpen]);

  const handleChildProfileClick = useCallback(
    (child: AdultAssociation) => {
      navigate(
        {
          pathname: getRouteModalPathname('child', child),
          search: `?context=${UserContexts.Individual}`,
        },
        { state: { backgroundLocation: location } },
      );
      onClick();
    },
    [onClick, navigate, location],
  );

  return (
    <Stack overflow="hidden" sx={{ maxHeight: 'calc(100vh - 200)' }}>
      <Stack px={1} pt={2} pb={1} gap={1}>
        <Stack px={1}>
          <Typography variant="h3">
            <FormattedMessage id="peopleDetail-MyProfile" />
          </Typography>
        </Stack>
        <UserItem user={user} onClick={handleMyProfileClick} subtitle={user.account_email} />
      </Stack>
      {isParent && (
        <>
          <Divider />
          <Stack pt={2} gap={1}>
            <Stack px={2}>
              <Typography variant="h3">
                <FormattedMessage id="peopleDetail-MyChildren" />
              </Typography>
            </Stack>
            <Stack px={1} pb={1} sx={{ overflow: 'scroll', maxHeight: 'calc(100vh - 400px)' }}>
              {user.adult_associations.map((u) => (
                <UserItem
                  key={u.user_id}
                  user={u}
                  onClick={() => handleChildProfileClick(u)}
                  subtitle={format(newDateTimezoneOffset(u.date_of_birth), 'dd MMM yyy')}
                />
              ))}
            </Stack>
          </Stack>
        </>
      )}
      <Divider />
      <Stack px={1} py={0.5} alignSelf="flex-start" alignItems="flex-start">
        <Button variant="text" size="large" onClick={logOut} data-test-id="logout-nav-button">
          <FormattedMessage id="login-Logout" />
        </Button>
      </Stack>
    </Stack>
  );
};

const UserItem: FC<{ user: WithName & WithAvatar; subtitle: string; onClick: () => void }> = ({
  user,
  onClick,
  subtitle,
}) => {
  return (
    <Stack
      flexDirection="row"
      gap={1}
      sx={(theme) => ({
        cursor: 'pointer',
        background: theme.palette.background.paper,
        opacity: 1,
        px: 1,
        py: 1,
        borderRadius: theme.spacing(1),
        '&:hover': { background: theme.palette.background.default },
      })}
      onClick={onClick}
    >
      <AvatarAuth user={user} />
      <Stack>
        <Typography>{getUserFullName(user)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default UserNav;
